import type {AbstractPanel} from './stats-panel';
import {StatsTicker} from './stats-ticker';
import type {IStatsConfig, RenderingContext} from './types';

export class Stats {
    private _dom: HTMLElement;
    private _ticker: StatsTicker;
    private _config: IStatsConfig;
    private _activePanelID = 0;
    private _panels: AbstractPanel[] = [];

    public constructor(config: Partial<IStatsConfig>) {
        this._config = this._getConfig(config);
        this._ticker = this._getTicker();
        this._dom = this._getDom();
        this.dom.style.zIndex = '1000';
    }

    public get dom(): HTMLElement {
        return this._dom;
    }

    public get panels(): AbstractPanel[] {
        return this._panels;
    }

    private get _glContext(): RenderingContext | undefined {
        return this._config.gl;
    }

    public show(): void {
        this.dom.style.visibility = 'visible';
    }

    public hide(): void {
        this.dom.style.visibility = 'hidden';
    }

    public toggle(): void {
        this.dom.style.visibility = this.dom.style.visibility === 'hidden' ? 'visible' : 'hidden';
    }

    public destroy(): void {
        this._activePanelID = 0;

        while (this._panels.length > 0) {
            this.removePanelByIndex(0);
        }

        this.dom.remove();
    }

    public addPanel<T extends AbstractPanel>(panel: T): T {
        this._hidePanels();

        this._dom.appendChild(panel.dom);
        this._panels.push(panel);
        panel.init(this._config.panel);
        panel.onInstall(this._glContext);

        this.showPanel(this._activePanelID);

        return panel;
    }

    public removePanel<T extends AbstractPanel>(panel: T): T {
        this._dom.removeChild(panel.dom);
        this._panels.splice(this._panels.indexOf(panel), 1);
        panel.onUninstall();

        return panel;
    }

    public removePanelByIndex(id: number): AbstractPanel {
        return this.removePanel(this._panels[id]);
    }

    public showPanel(id: number): void {
        if (id >= this._panels.length) {
            throw Error(`Stats: showPanel > Provided id ${id} is incorrect`);
        }

        this._hidePanels();

        this._activePanelID = id;
        this._panels[id].dom.style.display = 'block';

        this._resetClickListener();
    }

    public showAllPanels(): void {
        this._activePanelID = 0;
        this._panels.forEach((p, i) => (p.dom.style.display = 'block'));

        this._removeClickListener();
    }

    public readonly _onClick = (): void => {
        this.showPanel(++this._activePanelID % this._dom.children.length);
    };

    public readonly update = (): void => {
        this._ticker.update();
        this._panels.forEach((p) => p.update());
    };

    private readonly _render = (): void => {
        this._panels.forEach((p) => p.render());
    };

    private _hidePanels(): void {
        this._panels.forEach((p) => (p.dom.style.display = 'none'));
    }

    private _getTicker(): StatsTicker {
        const ticker = new StatsTicker(this._config.renderStep);

        ticker.register(this._render);

        return ticker;
    }

    private _getDom(): HTMLElement {
        const { id, css, parent } = this._config;

        const dom = document.createElement('div');

        dom.id = id;
        dom.style.cssText = css;

        parent.appendChild(dom);

        return dom;
    }

    private _resetClickListener(): void {
        this._removeClickListener();
        this._addClickListener();
    }

    private _addClickListener(): void {
        this._dom.style.cursor = 'pointer';
        this._dom.addEventListener('click', this._onClick, false);
    }

    private _removeClickListener(): void {
        this._dom.style.cursor = 'default';
        this._dom.removeEventListener('click', this._onClick, false);
    }

    private _getConfig(config: Partial<IStatsConfig>): IStatsConfig {
        return Object.assign(
            {
                renderStep: 500,
                id: 'stats',
                parent: document.body,
                css: 'position:absolute; top:0; left:0; transform-origin:top left; opacity:1; scale:0.57',
                panel: { width: 70, height: 45 }
            },
            config
        );
    }
}
